import React from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import { styled } from "@mui/material/styles";
import { TooltipComponentProps } from "../../interface";
import { PRIMARY_INPUT_CAN_HOVER } from "../../utils/touchInput";

/**
 * MUI's Tooltip where the primary input cannot hover (a phone, a tablet), where a touch never opens it: a
 * tooltip opened on a long press is positioned once, so it stays behind when what it labels moves (a
 * draggable toolbar). The hover listener is off too: MUI ignores the mouseover a tap leaves behind only in
 * the tooltip instance that saw the touch, so one remounted in between (a new key, or a styled wrapper
 * created during render) opens on it. Keyboard focus still opens it, and the title still names the child
 * (MUI sets it as the child's aria-label), the only name an icon-only button has.
 */
function TouchOnlyTooltip(props: TooltipProps): React.JSX.Element {
  return <Tooltip {...props} disableHoverListener disableTouchListener />;
}

/**
 * MUI's Tooltip with MUI's own props and look, for a caller that needs those rather than
 * ToolTipComponent's, which renders through it too. Where the primary input can hover (a desktop, a laptop
 * with a touch screen) this is MUI's Tooltip itself.
 */
export const PlainTooltip: (props: TooltipProps) => React.JSX.Element = PRIMARY_INPUT_CAN_HOVER
  ? Tooltip
  : TouchOnlyTooltip;

/** TapTooltip keeps its own open state; a click on its child is what toggles it on a touch-only device. */
export type TapTooltipProps = Omit<TooltipProps, "children" | "open" | "onOpen" | "onClose"> & {
  children: React.ReactElement<{ onClick?: React.MouseEventHandler<HTMLElement> | undefined }>;
};

function TouchOnlyTapTooltip({ children, ...props }: TapTooltipProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const close = (): void => setOpen(false);
  return (
    // Closed by a click elsewhere, not a touch: a finger scrolling the page leaves it open. A click inside
    // the tooltip counts as elsewhere, after the control clicked there has run.
    <ClickAwayListener mouseEvent="onClick" touchEvent={false} onClickAway={close}>
      <Tooltip
        {...props}
        open={open}
        // Only keyboard focus still opens it (see TouchOnlyTooltip); blur and Escape close it.
        onOpen={() => setOpen(true)}
        onClose={close}
        disableHoverListener
        disableTouchListener
      >
        {React.cloneElement(children, {
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            children.props.onClick?.(event);
            setOpen((wasOpen) => !wasOpen);
          },
        })}
      </Tooltip>
    </ClickAwayListener>
  );
}

/**
 * PlainTooltip for a title a phone user must still reach: an action, or text shown nowhere else. Where the
 * primary input can hover this is MUI's Tooltip itself. Where it cannot, a click on the child (a tap) opens
 * and closes it and a click anywhere else closes it; MUI's long-press timer and the mouseover a tap leaves
 * behind still do not open it. Not inside a DraggablePanel: a tooltip does not follow a dragged panel, so
 * one left open there stays behind.
 */
export const TapTooltip: (props: TapTooltipProps) => React.JSX.Element = PRIMARY_INPUT_CAN_HOVER
  ? Tooltip
  : TouchOnlyTapTooltip;

interface StyledTooltipProps extends TooltipProps {
  tooltipStyles?: React.CSSProperties;
}

const StyledTooltip = styled(
  // `className` is injected by styled() but typed optional, so it is spread in only when present:
  // MUI's `TooltipClasses` keys are `string`, and an explicit `undefined` popper class is not the
  // same as an absent one under exactOptionalPropertyTypes.
  ({ className, ...props }: StyledTooltipProps) => (
    <PlainTooltip {...props} classes={{ ...(className !== undefined && { popper: className }) }} />
  ),
  {
    shouldForwardProp: (prop) => prop !== "tooltipStyles",
  },
)(({ tooltipStyles }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "var(--primary-text)",
    color: "var(--surface-white)",
    fontSize: "0.875rem",
    fontWeight: 500,
    fontFamily: "inherit",
    padding: "0.5rem 0.75rem",
    borderRadius: "0.25rem",
    gap: "0.5rem",
    ...tooltipStyles,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "var(--primary-text)",
  },
}));

const TOOLTIP_Z_INDEX = 13000;
// Stable references: a fresh `slots`/`slotProps` object each render makes MUI re-run useSlot and
// remount the Zoom transition, which can schedule an update → re-render → new object → render loop.
const TOOLTIP_SLOTS = { transition: Zoom };

const ToolTipComponent: React.FC<TooltipComponentProps> = (props) => {
  // `showTipPointer` is the Syncfusion name for the arrow, which this tooltip always draws: it is read off here, since
  // MUI hands every prop it does not know to the anchor element, where React warns about it.
  const { content, children, position, slotProps, showTipPointer: _showTipPointer, ...rest } = props;
  const hasContent = !!content;

  const mergedSlotProps = React.useMemo(
    () => ({
      ...slotProps,
      popper: {
        ...slotProps?.popper,
        style: { zIndex: TOOLTIP_Z_INDEX, ...slotProps?.popper?.style },
      },
    }),
    [slotProps],
  );

  return (
    <StyledTooltip
      {...rest}
      slotProps={mergedSlotProps}
      placement={position || "top"}
      slots={TOOLTIP_SLOTS}
      arrow
      title={hasContent ? content : ""}
    >
      {/* MUI clones this child to attach the ref and the hover/focus handlers, so it must be a
          single element. `TooltipComponentProps.children` is a `ReactNode`, so anything that is not
          already an element (text, a number, `false`, nullish) is wrapped instead of asserted. */}
      {React.isValidElement(children) ? children : <span>{children}</span>}
    </StyledTooltip>
  );
};

ToolTipComponent.displayName = "ToolTipComponent";
export default ToolTipComponent;
